// Every open change a client has asked for, wherever they asked for it.
//
// Revisions used to read one table: `approvals` with `revision-requested`.
// Clients do almost all of their asking somewhere else, in the review
// workspaces, as pinned notes on a document, a website page or a video frame.
// None of those reached Revisions, so the page said "none" while fourteen
// document notes and fifty-four website notes sat open.
//
// This turns all four sources into one shape and one rule, so the page, the
// sidebar badge and Home all count the same thing.
//
// A change request is:
//   * a top-level comment (a reply is discussion on its parent, not a change),
//   * that the client can see (team-only notes are the studio talking to
//     itself, not something the client is waiting on),
//   * that nobody has resolved or closed as won't fix,
//   * on a review that is still live (an archived review is a closed link).
//   * Or: an approval the client answered with "revision requested", once.
//
// Pure on purpose, except `loadChangeRequests` and `resolveChangeRequest`,
// which take the Supabase client as an argument so tests can hand in a fake.

import { isResolved, isRoot } from './reviewThreads'
import { isFromClient, isTeamOnly } from './reviewVisibility'
import { pageLabel } from './reviewPipeline'
import { approvalTitle } from './approvalPipeline'
import { formatRange } from './timecode'

export const SOURCES = [
  { key: 'document', label: 'Documents', one: 'Document' },
  { key: 'website', label: 'Websites', one: 'Website' },
  { key: 'video', label: 'Videos', one: 'Video' },
  { key: 'approval', label: 'Approvals', one: 'Approval' },
]

const SOURCE_BY_KEY = Object.fromEntries(SOURCES.map(s => [s.key, s]))

/** Older than this many days, a waiting change is drawn as a problem. */
export const STALE_AFTER_DAYS = 5

const DAY = 86400000
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate())

/** Whole calendar days since `raw`. Never negative; null for no date. */
export function ageInDays(raw, now = new Date()) {
  if (!raw) return null
  const t = new Date(raw)
  if (Number.isNaN(t.getTime())) return null
  return Math.max(0, Math.round((startOfDay(now) - startOfDay(t)) / DAY))
}

/** "Today", "Yesterday", "4 days ago". */
export function describeAge(days) {
  if (days == null) return ''
  if (days <= 0) return 'Today'
  if (days === 1) return 'Yesterday'
  return `${days} days ago`
}

export const isStale = (item) => (item?.ageDays ?? 0) > STALE_AFTER_DAYS

const clientNameOf = (row) => row?.clients?.company || row?.clients?.name || 'No client'
const trimText = (s) => String(s || '').replace(/\s+/g, ' ').trim()
const isArchived = (review) => String(review?.status || '').toLowerCase() === 'archived'

/** A comment that counts as a change request. */
export function isOpenChange(comment) {
  return !!comment && isRoot(comment) && !isResolved(comment) && !isTeamOnly(comment)
}

/** Replies under each root, counted through any depth. */
function replyCounts(comments) {
  const byId = new Map(comments.map(c => [c.id, c]))
  const out = new Map()
  for (const c of comments) {
    if (!c.parent_id) continue
    let p = byId.get(c.parent_id)
    let depth = 0
    while (p && p.parent_id && depth < 50) { p = byId.get(p.parent_id); depth += 1 }
    if (p) out.set(p.id, (out.get(p.id) || 0) + 1)
  }
  return out
}

function authorOf(comment, review) {
  if (isFromClient(comment)) return trimText(comment.author_name) || review?.clients?.name || 'Client'
  return 'Team'
}

function base(source, review, comment, now, replies) {
  const createdAt = comment.created_at || null
  return {
    id: `${source}:${comment.id}`,
    commentId: comment.id,
    reviewId: review.id,
    source,
    sourceLabel: SOURCE_BY_KEY[source].one,
    clientId: review.client_id || null,
    client: clientNameOf(review),
    projectId: review.project_id || null,
    project: review.projects?.title || null,
    text: trimText(comment.body) || 'No text, see the pin',
    author: authorOf(comment, review),
    fromClient: isFromClient(comment),
    createdAt,
    ageDays: ageInDays(createdAt, now),
    replies: replies.get(comment.id) || 0,
    status: 'open',
    resolvable: true,
  }
}

/** Document reviews, with `document_review_comments` and `document_review_files` embedded. */
export function fromDocumentReviews(reviews = [], now = new Date()) {
  const out = []
  for (const review of reviews || []) {
    if (!review || isArchived(review)) continue
    const comments = (review.document_review_comments || []).filter(Boolean)
    const files = new Map((review.document_review_files || []).map(f => [f.id, f]))
    const replies = replyCounts(comments)
    for (const c of comments.filter(isOpenChange)) {
      const file = files.get(c.file_id)
      const version = c.version || file?.version || review.current_version || 1
      const where = [file?.name, c.page ? `page ${c.page}` : null].filter(Boolean).join(', ')
      out.push({
        ...base('document', review, c, now, replies),
        itemTitle: `${trimText(review.title) || 'Untitled document'} · v${version}`,
        location: where || null,
        link: `/reviews-inbox/doc/${review.id}?comment=${c.id}`,
      })
    }
  }
  return out
}

/** Website reviews, with `review_comments` and `review_pages` embedded. */
export function fromWebsiteReviews(reviews = [], now = new Date()) {
  const out = []
  for (const review of reviews || []) {
    if (!review || isArchived(review)) continue
    const comments = (review.review_comments || []).filter(Boolean)
    const pages = new Map((review.review_pages || []).map(p => [p.id, p]))
    const replies = replyCounts(comments)
    for (const c of comments.filter(isOpenChange)) {
      const page = pages.get(c.page_id)
      const version = c.version || review.current_version || 1
      const where = [page ? pageLabel(page) : null, trimText(c.element_label) || null].filter(Boolean).join(', ')
      out.push({
        ...base('website', review, c, now, replies),
        itemTitle: `${trimText(review.name) || 'Untitled website'} · v${version}`,
        location: where || null,
        link: `/reviews-inbox/web/${review.id}?comment=${c.id}`,
      })
    }
  }
  return out
}

/** Video reviews, with `video_review_comments` and `video_review_versions` embedded. */
export function fromVideoReviews(reviews = [], now = new Date()) {
  const out = []
  for (const review of reviews || []) {
    if (!review || isArchived(review)) continue
    const comments = (review.video_review_comments || []).filter(Boolean)
    const versions = new Map((review.video_review_versions || []).map(v => [v.id, v]))
    const replies = replyCounts(comments)
    for (const c of comments.filter(isOpenChange)) {
      const version = versions.get(c.version_id)?.version || review.current_version || 1
      const at = c.time_s != null ? formatRange(c.time_s, c.end_s) : ''
      out.push({
        ...base('video', review, c, now, replies),
        itemTitle: `${trimText(review.title) || 'Untitled video'} · v${version}`,
        location: at ? `at ${at}` : null,
        link: `/reviews-inbox/video/${review.id}?comment=${c.id}`,
      })
    }
  }
  return out
}

/** Approvals the client sent back. One change request each. */
export function fromApprovals(approvals = [], now = new Date()) {
  return (approvals || [])
    .filter(a => a && String(a.status || '').toLowerCase().replace('_', '-') === 'revision-requested')
    .map(a => {
      const createdAt = a.responded_at || a.created_at || null
      return {
        id: `approval:${a.id}`,
        commentId: null,
        reviewId: a.id,
        source: 'approval',
        sourceLabel: SOURCE_BY_KEY.approval.one,
        clientId: a.client_id || null,
        client: clientNameOf(a),
        projectId: a.project_id || null,
        project: a.projects?.title || null,
        itemTitle: approvalTitle(a),
        text: trimText(a.feedback) || 'Asked for changes without leaving a note',
        author: a.clients?.name || 'Client',
        fromClient: true,
        createdAt,
        ageDays: ageInDays(createdAt, now),
        replies: 0,
        location: null,
        link: '/approvals',
        status: 'open',
        resolvable: true,
        approval: a,
      }
    })
}

/** Oldest first, so the change that has waited longest is read first. */
const byAge = (a, b) => (b.ageDays ?? -1) - (a.ageDays ?? -1)
  || String(a.createdAt || '').localeCompare(String(b.createdAt || ''))
  || String(a.id).localeCompare(String(b.id))

/** All four sources, normalised and sorted oldest first. */
export function buildChangeRequests({ documents = [], websites = [], videos = [], approvals = [] } = {}, now = new Date()) {
  return [
    ...fromDocumentReviews(documents, now),
    ...fromWebsiteReviews(websites, now),
    ...fromVideoReviews(videos, now),
    ...fromApprovals(approvals, now),
  ].sort(byAge)
}

/** The numbers strip: how many, the oldest, and how many from each source. */
export function summarizeChangeRequests(items = []) {
  const bySource = Object.fromEntries(SOURCES.map(s => [s.key, 0]))
  let oldestDays = null
  for (const i of items || []) {
    bySource[i.source] = (bySource[i.source] || 0) + 1
    if (i.ageDays != null && (oldestDays == null || i.ageDays > oldestDays)) oldestDays = i.ageDays
  }
  return { open: (items || []).length, oldestDays, bySource }
}

/** The list filtered by source, client and project. Empty values mean "all". */
export function filterChangeRequests(items = [], { source = '', clientId = '', projectId = '' } = {}) {
  return (items || []).filter(i =>
    (!source || i.source === source)
    && (!clientId || i.clientId === clientId)
    && (!projectId || i.projectId === projectId))
}

/**
 * Grouped by project, the group that has waited longest first. Work with no
 * project is grouped by client, so it still sits beside its own kind.
 */
export function groupByProject(items = []) {
  const groups = new Map()
  for (const i of items || []) {
    const key = i.projectId ? `project:${i.projectId}` : `client:${i.clientId || 'none'}`
    if (!groups.has(key)) {
      groups.set(key, { key, projectId: i.projectId, project: i.project || 'No project', clientId: i.clientId, client: i.client, items: [] })
    }
    groups.get(key).items.push(i)
  }
  return [...groups.values()]
    .map(g => ({ ...g, items: g.items.slice().sort(byAge), oldestDays: Math.max(...g.items.map(i => i.ageDays ?? 0)) }))
    .sort((a, b) => b.oldestDays - a.oldestDays || b.items.length - a.items.length || a.project.localeCompare(b.project))
}

/** Client and project choices for the filters, from the items themselves. */
export function filterOptions(items = []) {
  const clients = new Map()
  const projects = new Map()
  for (const i of items || []) {
    if (i.clientId && !clients.has(i.clientId)) clients.set(i.clientId, i.client)
    if (i.projectId && !projects.has(i.projectId)) projects.set(i.projectId, { name: i.project || 'Untitled project', clientId: i.clientId })
  }
  return {
    clients: [...clients.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)),
    projects: [...projects.entries()].map(([id, p]) => ({ id, ...p })).sort((a, b) => a.name.localeCompare(b.name)),
  }
}

// ---------------------------------------------------------------------------
// Loading and resolving. The client is passed in; nothing here imports it.

const missingTable = (error) => {
  if (!error) return false
  const code = String(error.code || '')
  const msg = String(error.message || '').toLowerCase()
  return code === '42P01' || code === 'PGRST205' || code === 'PGRST200'
    || (msg.includes('relation') && msg.includes('does not exist'))
    || msg.includes('could not find the table')
}

const QUERIES = {
  documents: (db) => db.from('document_reviews')
    .select('id, title, status, current_version, client_id, project_id, clients(name, company), projects(title), document_review_files!document_review_files_review_id_fkey(id, name, version), document_review_comments!document_review_comments_review_id_fkey(id, parent_id, file_id, page, x_pct, body, author_type, author_name, status, visibility, version, created_at)'),
  websites: (db) => db.from('website_reviews')
    .select('id, name, status, current_version, client_id, project_id, clients(name, company), projects(title), review_pages!review_pages_review_id_fkey(id, url, label), review_comments!review_comments_review_id_fkey(id, parent_id, page_id, element_label, body, author_type, author_name, status, visibility, version, created_at)'),
  videos: (db) => db.from('video_reviews')
    .select('id, title, status, current_version, client_id, project_id, clients(name, company), projects(title), video_review_versions!video_review_versions_review_id_fkey(id, version), video_review_comments!video_review_comments_review_id_fkey(id, parent_id, version_id, time_s, end_s, body, author_type, author_name, status, visibility, created_at)'),
  approvals: (db) => db.from('approvals')
    .select('*, clients(name, company), projects(title), deliverables(id, title, revisions_used, revisions_max)')
    .eq('status', 'revision-requested'),
}

const LABELS = { documents: 'Documents', websites: 'Websites', videos: 'Videos', approvals: 'Approvals' }

/**
 * Every open change request, from every source that answers.
 *
 * Each source fails on its own: a broken query is reported in `errors` and the
 * others still count. A video table that does not exist yet is not an error,
 * it simply has nothing to say.
 */
export async function loadChangeRequests(db, now = new Date()) {
  const keys = Object.keys(QUERIES)
  const results = await Promise.all(keys.map(async (key) => {
    try {
      const { data, error } = await QUERIES[key](db)
      if (error) return { key, data: [], error: missingTable(error) ? null : error }
      return { key, data: data || [], error: null }
    } catch (error) {
      return { key, data: [], error }
    }
  }))
  const rows = Object.fromEntries(results.map(r => [r.key, r.data]))
  const errors = results.filter(r => r.error).map(r => ({ source: r.key, label: LABELS[r.key], message: r.error.message || String(r.error) }))
  return { items: buildChangeRequests(rows, now), errors }
}

/** The badge number. Zero when nothing could be read. */
export async function countOpenChangeRequests(db, now = new Date()) {
  try {
    const { items } = await loadChangeRequests(db, now)
    return items.length
  } catch {
    return 0
  }
}

/**
 * Close one change request, through the same write its own workspace uses:
 *   document  document_review_comments.status/resolved_at (ReviewDocWorkspace)
 *   video     video_review_comments.status/resolved_at   (ReviewVideoWorkspace)
 *   website   rpc set_review_comment_status              (ReviewWebWorkspace)
 *   approval  back to `pending`, one revision round spent (the old Revisions page)
 * Returns { error } with error null on success.
 */
export async function resolveChangeRequest(db, item, nowIso = new Date().toISOString()) {
  try {
    if (item.source === 'document' || item.source === 'video') {
      const table = item.source === 'document' ? 'document_review_comments' : 'video_review_comments'
      const { error } = await db.from(table).update({ status: 'resolved', resolved_at: nowIso }).eq('id', item.commentId)
      return { error: error || null }
    }
    if (item.source === 'website') {
      const { error } = await db.rpc('set_review_comment_status', { comment_id: item.commentId, new_status: 'resolved' })
      return { error: error || null }
    }
    if (item.source === 'approval') {
      const { error } = await db.from('approvals').update({ status: 'pending', responded_at: null }).eq('id', item.reviewId)
      if (error) return { error }
      // The round is spent when the studio delivers it, not when the client
      // asks. A failure here does not undo the send-back.
      const d = item.approval?.deliverables
      if (d?.id) {
        const used = Number.isFinite(d.revisions_used) ? d.revisions_used : 0
        const { error: cErr } = await db.from('deliverables').update({ revisions_used: used + 1 }).eq('id', d.id)
        if (cErr) console.error('Revision counter update failed:', cErr)
      }
      return { error: null }
    }
    return { error: new Error('This change has no resolve path.') }
  } catch (error) {
    return { error }
  }
}
